import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminLiveComment, LiveCommentStatus } from "@tih/shared";
import {
  adminDeleteLiveComment,
  adminFetchLiveComments,
  adminModerateLiveComment,
  adminSyncYoutubeComments,
} from "../../api/client";

const TABS: { key: LiveCommentStatus | "ALL"; label: string }[] = [
  { key: "PENDING", label: "Pending" },
  { key: "PUBLISHED", label: "Published" },
  { key: "REJECTED", label: "Rejected" },
  { key: "ALL", label: "All" },
];

export function AdminCommentsPage() {
  const [tab, setTab] = useState<LiveCommentStatus | "ALL">("PENDING");
  const [items, setItems] = useState<AdminLiveComment[]>([]);
  const [youtubeSyncEnabled, setYoutubeSyncEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncBusy, setSyncBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetchLiveComments(tab === "ALL" ? undefined : tab);
      setItems(res.comments);
      setYoutubeSyncEnabled(res.youtubeSyncEnabled);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function moderate(id: number, status: "PUBLISHED" | "REJECTED") {
    await adminModerateLiveComment(id, status);
    await load();
  }

  async function remove(id: number) {
    if (!confirm("Delete this comment permanently?")) return;
    await adminDeleteLiveComment(id);
    await load();
  }

  async function syncYoutube() {
    setSyncBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminSyncYoutubeComments();
      setSuccess(
        result.synced > 0
          ? `Synced ${result.synced} YouTube comment${result.synced === 1 ? "" : "s"} from ${result.videos.length} video${result.videos.length === 1 ? "" : "s"}.`
          : "YouTube sync finished — no new comments (or videos have no YouTube source).",
      );
      await load();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setSyncBusy(false);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Live comments</h1>
          <p>
            Moderate site + YouTube comments from the live room and video watch pages
            (<code>/videos/view/:id</code>). Site comments need approval before they appear.
            Watch-page comments are stored as <code>LiveComment</code> rows with a{" "}
            <code>youtubeVideoId</code>.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={syncBusy || !youtubeSyncEnabled}
          onClick={() => void syncYoutube()}
          title={
            youtubeSyncEnabled
              ? "Pull latest comments from YouTube videos on the live page"
              : "Add YOUTUBE_API_KEY to enable YouTube sync"
          }
        >
          {syncBusy ? "Syncing…" : "Sync YouTube"}
        </button>
      </header>

      {!youtubeSyncEnabled && (
        <div className="admin-event-context-banner" style={{ marginBottom: 16 }}>
          YouTube sync is off — set <code>YOUTUBE_API_KEY</code> in server env to import
          comments from YouTube replays alongside site comments.
        </div>
      )}

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? "active" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}
      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ opacity: 0.6 }}>Nothing here.</div>
      ) : (
        <ul className="admin-cards">
          {items.map((c) => (
            <li key={c.id} className="admin-card">
              <p style={{ margin: 0 }}>{c.body}</p>
              <div className="admin-card-meta">
                <strong>{c.authorName}</strong>
                {c.authorEmail && <span>· {c.authorEmail}</span>}
                <span>· {new Date(c.createdAt).toLocaleString()}</span>
                <span className={`status-pill status-${c.status.toLowerCase()}`}>
                  {c.status}
                </span>
                <span className="admin-badge admin-badge-muted">{c.source}</span>
                {c.youtubeVideoId && (
                  <span>
                    ·{" "}
                    <Link to={`/videos/view/${encodeURIComponent(c.youtubeVideoId)}`}>
                      Watch page
                    </Link>
                    {" · "}
                    <a
                      href={`https://www.youtube.com/watch?v=${encodeURIComponent(c.youtubeVideoId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      YouTube
                    </a>
                  </span>
                )}
              </div>
              <div className="admin-card-actions">
                {c.status !== "PUBLISHED" && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void moderate(c.id, "PUBLISHED")}
                  >
                    Approve
                  </button>
                )}
                {c.status !== "REJECTED" && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void moderate(c.id, "REJECTED")}
                  >
                    Reject
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void remove(c.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
